export interface ColumnMapping {
  csvColumn: string;
  mylesField: string;
  table: "contact" | "company";
}

export interface SourcePreset {
  name: string;
  key: "hubspot" | "pipedrive" | "linkedin" | "clay" | "custom_csv";
  mappings: ColumnMapping[];
}

export const SOURCE_PRESETS: SourcePreset[] = [
  {
    name: "HubSpot",
    key: "hubspot",
    mappings: [
      { csvColumn: "First Name", mylesField: "first_name", table: "contact" },
      { csvColumn: "Last Name", mylesField: "last_name", table: "contact" },
      { csvColumn: "Email", mylesField: "email", table: "contact" },
      { csvColumn: "Company Name", mylesField: "name", table: "company" },
      { csvColumn: "Job Title", mylesField: "title", table: "contact" },
      { csvColumn: "Phone Number", mylesField: "phone", table: "contact" },
      { csvColumn: "LinkedIn", mylesField: "linkedin_url", table: "contact" },
      { csvColumn: "Industry", mylesField: "industry", table: "company" },
      { csvColumn: "City", mylesField: "city", table: "company" },
      {
        csvColumn: "Country/Region",
        mylesField: "hq_country",
        table: "company",
      },
    ],
  },
  {
    name: "Pipedrive",
    key: "pipedrive",
    mappings: [
      { csvColumn: "Name", mylesField: "full_name", table: "contact" },
      { csvColumn: "Organization", mylesField: "name", table: "company" },
      { csvColumn: "Email", mylesField: "email", table: "contact" },
      { csvColumn: "Phone", mylesField: "phone", table: "contact" },
      { csvColumn: "Title", mylesField: "title", table: "contact" },
    ],
  },
  {
    name: "Clay",
    key: "clay",
    mappings: [
      { csvColumn: "First Name", mylesField: "first_name", table: "contact" },
      { csvColumn: "Last Name", mylesField: "last_name", table: "contact" },
      { csvColumn: "Work Email", mylesField: "email", table: "contact" },
      { csvColumn: "Company Name", mylesField: "name", table: "company" },
      { csvColumn: "Job Title", mylesField: "title", table: "contact" },
      {
        csvColumn: "LinkedIn Profile",
        mylesField: "linkedin_url",
        table: "contact",
      },
      { csvColumn: "Company Domain", mylesField: "website", table: "company" },
      { csvColumn: "Naics Industry", mylesField: "industry", table: "company" },
      { csvColumn: "Country Name", mylesField: "hq_country", table: "company" },
      { csvColumn: "City Name", mylesField: "city", table: "company" },
      {
        csvColumn: "Employee Count",
        mylesField: "employee_count",
        table: "company",
      },
      { csvColumn: "Location", mylesField: "address", table: "company" },
      {
        csvColumn: "Revenue Band",
        mylesField: "revenue",
        table: "company",
      },
      {
        csvColumn: "Latest Annual Revenue",
        mylesField: "revenue_tier",
        table: "company",
      },
      { csvColumn: "Headline", mylesField: "background", table: "contact" },
      {
        csvColumn: "Summary",
        mylesField: "innovation_experience",
        table: "contact",
      },
    ],
  },
  {
    name: "LinkedIn",
    key: "linkedin",
    mappings: [
      { csvColumn: "First Name", mylesField: "first_name", table: "contact" },
      { csvColumn: "Last Name", mylesField: "last_name", table: "contact" },
      { csvColumn: "Company", mylesField: "name", table: "company" },
      { csvColumn: "Position", mylesField: "title", table: "contact" },
      {
        csvColumn: "Connected On",
        mylesField: "connected_on",
        table: "contact",
      },
      { csvColumn: "URL", mylesField: "linkedin_url", table: "contact" },
      { csvColumn: "Email Address", mylesField: "email", table: "contact" },
    ],
  },
];

export const MYLES_CONTACT_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "linkedin_url",
  "title",
  "function_role",
  "seniority_level",
  "tenure_years",
  "is_budget_owner",
  "budget_scope",
  "mint_background",
  "innovation_experience",
  "linkedin_activity",
  "social_engagement",
  "source_origin",
  "background",
] as const;

export const MYLES_COMPANY_FIELDS = [
  "name",
  "industry",
  "hq_region",
  "hq_country",
  "business_model",
  "revenue_tier",
  "weltmarkt_ranking",
  "website",
  "employee_count",
  "city",
] as const;

/** Auto-map CSV headers to MYLES fields using a source preset */
export function autoMapColumns(
  csvHeaders: string[],
  sourceKey: SourcePreset["key"],
): Record<string, { mylesField: string; table: "contact" | "company" }> {
  const preset = SOURCE_PRESETS.find((p) => p.key === sourceKey);
  if (!preset) return {};

  const mapping: Record<
    string,
    { mylesField: string; table: "contact" | "company" }
  > = {};
  for (const header of csvHeaders) {
    const match = preset.mappings.find(
      (m) => m.csvColumn.toLowerCase() === header.toLowerCase(),
    );
    if (match) {
      mapping[header] = { mylesField: match.mylesField, table: match.table };
    }
  }
  return mapping;
}
